import { Box, Flex, Heading, Spinner, Stack, Text } from '@chakra-ui/react'
import { ApiError } from '@scout/identity'
import { SparkleIcon } from '@scout/ui/icons'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useCallback } from 'react'

import { useHeldBrief, useSignup } from '@/features/brief/api'
import { SignUp } from '@/features/brief/SignUp'
import { StartOver } from '@/features/brief/StartOver'
import { Workspace } from '@/features/brief/Workspace'
import { VisitorOnly } from '@/features/identity/VisitorOnly'

// Hoisted: an object literal in JSX would be a new prop on every render.
const gutter = { base: 4, md: 7 }

export const Route = createFileRoute('/try/brief')({
  component: HeldBrief,
})

/**
 * Bound above the page rather than inside it, so a member's redirect resolves
 * before the held brief is ever asked for.
 */
function HeldBrief() {
  return (
    <VisitorOnly>
      <HeldBriefRoute />
    </VisitorOnly>
  )
}

/**
 * What a holder can do with the brief besides talk to it.
 *
 * Two things, and they are not alternatives. Signing up keeps this brief:
 * signing in establishes the buyer and claims it, so it then belongs to an
 * organisation, membership rather than the cookie answers for it, and it moves
 * to an address that can be shared. Starting over puts it down: a holder gets
 * one brief at a time, and this is how they change which one.
 *
 * Starting over is offered here whether or not signing up is, because a holder
 * who started a brief about the wrong thing needs a way out of it that does not
 * depend on an authentication boundary being configured.
 */
function HolderAside() {
  const navigate = useNavigate()
  const signup = useSignup()

  const onSignedIn = useCallback(
    (sessionToken: string) => {
      signup.mutate(sessionToken, {
        onSuccess: (result) => {
          if (result.brief !== null) {
            void navigate({ to: '/app/briefs/$briefId', params: { briefId: result.brief.id } })
          }
        },
      })
    },
    [signup, navigate],
  )

  // Already here, and the new brief is reached the same way this one was — so
  // this settles where the holder ends up rather than moving them.
  const onStarted = useCallback(() => {
    void navigate({ to: '/try/brief' })
  }, [navigate])

  return (
    /*
      One card, in the sage surface the panel keeps for a grouping that is not
      part of the brief itself. The eyebrow and the display face mark it as
      Scout speaking rather than as another section of what the buyer said.
    */
    <Box bg="bg.muted" borderRadius="l3" p="4" aria-label="Keep this brief">
      <Stack gap="3" align="stretch">
        <Flex align="center" gap="2.5">
          <SparkleIcon color="fg.muted" />
          <Text
            fontSize="xs"
            fontWeight="medium"
            letterSpacing="0.14em"
            textTransform="uppercase"
            color="fg.muted"
          >
            Get full access
          </Text>
        </Flex>

        <Heading
          as="h2"
          fontFamily="display"
          fontSize="xl"
          fontWeight="semibold"
          lineHeight="heading"
          letterSpacing="heading"
        >
          Keep this brief.
        </Heading>

        <SignUp onSignedIn={onSignedIn} />
        <StartOver onStarted={onStarted} />
      </Stack>
    </Box>
  )
}

// Hoisted: a JSX literal in a prop is a new element on every render.
const holderAside = <HolderAside />

/**
 * The anonymous brief this browser holds.
 *
 * There is no id in the path on purpose. The brief is reached through the
 * cookie that holds it, so a URL sent to someone else reaches nothing — which
 * is what stops the first person to sign up from a shared link taking a brief
 * they did not build.
 *
 * A member never gets here: `VisitorOnly` wraps it, so the redirect happens
 * above this component and none of its reads ever start. That matters beyond
 * tidiness — a member passing through must neither take a brief nor give one
 * up, and the surest way to guarantee it is for the requests not to be made.
 */
function HeldBriefRoute() {
  const brief = useHeldBrief()

  // A 404 is an answer, not a failure: this browser is holding nothing.
  const holdsNothing =
    brief.isError && brief.error instanceof ApiError && brief.error.status === 404

  if (brief.isPending) {
    return (
      <Box flex="1" px={gutter} py="6">
        <Spinner />
      </Box>
    )
  }

  if (holdsNothing) {
    return (
      <Box flex="1" overflowY="auto" px={gutter} py="6">
        <Text color="fg.muted" maxW="thread" mx="auto">
          This browser isn&rsquo;t holding a brief. Start one from the home page.
        </Text>
      </Box>
    )
  }

  if (brief.isError) {
    return (
      <Box flex="1" overflowY="auto" px={gutter} py="6">
        <Text role="alert" color="rust.fg" maxW="thread" mx="auto">
          {brief.error.message}
        </Text>
      </Box>
    )
  }

  return (
    <Workspace
      // Keyed by the brief this browser currently holds, so starting over tears
      // the old stream down and opens a new one. The path is the same either
      // way — the cookie is the address — so without the key a stream would go
      // on carrying the brief the cookie used to mean.
      key={brief.data.brief.id}
      streamPath="/brief/stream"
      messagesPath="/brief/messages"
      aside={holderAside}
    />
  )
}
